import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Prompt } from 'react-router-dom';
import { usePdfLayout } from '../../../hooks/pdfLayout';
import { TemplateEditorProvider } from '../../../contexts/TemplateEditorContext';
import { PdfLayout, PdfLayoutSection } from '../../../utils/api/pdfLayout';
import Error from '../../Shared/ResourceIndex/Error';
import Loading from '../../Shared/ResourceIndex/Loading';
import { defaultSection } from '../PdfLayout/DocumentPreview';
import PdfPanel, { defaultLayout } from '../PdfLayout/PdfPanel';
import StructurePanel from './StructurePanel';

export interface ITemplateEditorProps {
  templateId: number;
  templateName: string;
}

/**
 * Unified template builder: questions & structure on the left, the PDF
 * layout controls + live preview on the right
 */
const TemplateEditor: React.FC<ITemplateEditorProps> = ({
  templateId,
  templateName,
}) => {
  const { data, isLoading, isError } = usePdfLayout(templateId);

  const [sections, setSections] = useState<Record<number, PdfLayoutSection>>(
    {},
  );
  const [layout, setLayout] = useState<PdfLayout | null>(null);
  const [dirty, setDirty] = useState(false);
  const [mobileView, setMobileView] = useState<'structure' | 'preview'>(
    'structure',
  );
  const [layoutCollapsed, setLayoutCollapsed] = useState(false);
  const shellRef = useRef<HTMLDivElement>(null);
  const [topOffset, setTopOffset] = useState(200);

  // Fit the editor to the viewport below the app chrome so only the two
  // panels scroll — never the window.
  useLayoutEffect(() => {
    const measure = () => {
      if (shellRef.current) {
        setTopOffset(
          Math.round(
            shellRef.current.getBoundingClientRect().top + window.scrollY,
          ),
        );
      }
    };

    measure();
    window.addEventListener('resize', measure);

    return () => window.removeEventListener('resize', measure);
  }, []);

  // Both panes edit the layout — bands from the structure pane, page setup from
  // the PDF pane — so it is seeded once here and never reset by a refetch.
  useEffect(() => {
    if (data)
      setLayout(
        (prev) => prev ?? { ...defaultLayout(), ...(data.layout ?? {}) },
      );
  }, [data]);

  // Keep sections in sync with the group list so structure edits
  // (add/remove group) never orphan unsaved styling.
  useEffect(() => {
    if (!data) return;

    setSections((prev) => {
      const saved = Object.fromEntries(
        (data.layout?.sections ?? []).map((s) => [s.question_group_id, s]),
      );

      return Object.fromEntries(
        data.groups.map((group) => [
          group.id,
          prev[group.id] ?? saved[group.id] ?? defaultSection(group.id),
        ]),
      );
    });
  }, [data]);

  // <Prompt> only covers in-app navigation; a reload or tab close needs the
  // native dialog, and the listener must be off while clean so a saved editor
  useEffect(() => {
    if (!dirty) return;

    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'You have unsaved changes? Are you sure you want to leave?';
    };

    window.addEventListener('beforeunload', warn);

    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const updateSection = (
    groupId: number,
    updates: Partial<PdfLayoutSection>,
  ) => {
    setSections((prev) => ({
      ...prev,
      [groupId]: { ...prev[groupId], ...updates },
    }));
    setDirty(true);
  };

  const updateLayout = (updates: Partial<PdfLayout>) => {
    setLayout((prev) => prev && { ...prev, ...updates });
    setDirty(true);
  };

  if (isError) return <Error error='Failure to retrieve template' />;
  if (isLoading || !data || !layout) return <Loading />;

  return (
    <TemplateEditorProvider templateId={templateId} groups={data.groups}>
      <Prompt when={dirty} message='You have unsaved changes? Are you sure you want to leave?' />

      <div
        className={`template-builder is-mobile-${mobileView}${
          layoutCollapsed ? ' is-layout-collapsed' : ''
        }`}
        ref={shellRef}
        style={{ '--utb-top': `${topOffset}px` } as React.CSSProperties}
      >
        <div className='utb-mobile-tabs'>
          <button
            type='button'
            className={mobileView === 'structure' ? 'is-active' : ''}
            onClick={() => setMobileView('structure')}
          >
            Questions
          </button>
          <button
            type='button'
            className={mobileView === 'preview' ? 'is-active' : ''}
            onClick={() => setMobileView('preview')}
          >
            PDF Layout
          </button>
        </div>

        <StructurePanel
          groups={data.groups}
          layout={layout}
          sections={sections}
          logoGuid={data.logo_guid}
          onPatchLayout={updateLayout}
          onPatchSection={updateSection}
        />

        <button
          type='button'
          className='utb-layout-toggle'
          onClick={() => setLayoutCollapsed((prev) => !prev)}
          title={layoutCollapsed ? 'Expand PDF layout' : 'Collapse PDF layout'}
          aria-label={
            layoutCollapsed ? 'Expand PDF layout' : 'Collapse PDF layout'
          }
          aria-expanded={!layoutCollapsed}
        >
          <i
            className={`fa fa-step-${layoutCollapsed ? 'backward' : 'forward'}`}
          />
        </button>

        <PdfPanel
          templateName={templateName}
          layout={layout}
          sections={sections}
          groups={data.groups}
          logoGuid={data.logo_guid}
          dirty={dirty}
          setDirty={setDirty}
          collapsed={layoutCollapsed}
          onPatchLayout={updateLayout}
          onPatchSection={updateSection}
        />
      </div>
    </TemplateEditorProvider>
  );
};

export default TemplateEditor;
